'use client';

import { useState } from 'react';
import {
  Crown,
  ClipboardList,
  Check,
  X,
  AlertTriangle,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import type { StaffRole } from '@/types';

interface SettingsModuleProps {
  role: StaffRole;
  onResetDemo: () => void;
}

const MATRIX: { label: string; admin: boolean; desk: boolean }[] = [
  { label: 'Voir la flotte', admin: true, desk: true },
  { label: 'Créer une réservation', admin: true, desk: true },
  { label: 'Check-in / out + photos', admin: true, desk: true },
  { label: 'Voir les profits financiers', admin: true, desk: false },
  { label: 'Supprimer véhicules / écritures', admin: true, desk: false },
  { label: 'Gérer le personnel', admin: true, desk: false },
];

export function SettingsModule({ role, onResetDemo }: SettingsModuleProps) {
  const [confirmReset, setConfirmReset] = useState(false);

  return (
    <div className="space-y-4">
      {/* Role cards */}
      <div className="grid gap-3 md:grid-cols-2">
        <div
          className={`rounded-3xl border p-5 text-left ${
            role === 'super_admin' ? 'border-ink bg-ink text-shell' : 'border-line bg-card'
          }`}
        >
          <p className="flex items-center gap-2 text-sm font-bold">
            <Crown size={16} className={role === 'super_admin' ? 'text-[#FF8A00]' : 'text-[#FF8A00]'} />
            Super Admin / Agency Owner
            {role === 'super_admin' && (
              <span className="ml-auto rounded-full bg-[#FF8A00] px-2 py-0.5 text-[10px] font-bold text-slate-950">ACTIF</span>
            )}
          </p>
          <ul className={`mt-2 space-y-1 text-xs ${role === 'super_admin' ? 'text-shell/75' : 'text-muted'}`}>
            <li>✓ Accès complet : flotte, finance, profits</li>
            <li>✓ Suppression véhicules / écritures</li>
            <li>✓ Gestion du personnel + paramètres</li>
          </ul>
        </div>
        <div
          className={`rounded-3xl border p-5 text-left ${
            role === 'front_desk' ? 'border-ink bg-ink text-shell' : 'border-line bg-card'
          }`}
        >
          <p className="flex items-center gap-2 text-sm font-bold">
            <ClipboardList size={16} className="text-[#FF8A00]" />
            Front Desk / Parking Agent
            {role === 'front_desk' && (
              <span className="ml-auto rounded-full bg-[#FF8A00] px-2 py-0.5 text-[10px] font-bold text-slate-950">ACTIF</span>
            )}
          </p>
          <ul className={`mt-2 space-y-1 text-xs ${role === 'front_desk' ? 'text-shell/75' : 'text-muted'}`}>
            <li>✓ Créer réservations, check-in des voitures</li>
            <li>✗ PAS de profits, PAS de suppression</li>
            <li>✗ PAS de paramètres / personnel</li>
          </ul>
        </div>
      </div>

      {/* Permission matrix */}
      <div className="rounded-3xl border border-line bg-card p-4">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
          <ShieldCheck size={14} /> Matrice des permissions
        </p>
        <table className="mt-2 w-full text-xs">
          <thead>
            <tr className="text-left text-[11px] uppercase text-muted">
              <th className="py-2">Permission</th>
              <th className="py-2 text-center">Super Admin</th>
              <th className="py-2 text-center">Front Desk</th>
            </tr>
          </thead>
          <tbody>
            {MATRIX.map((m) => (
              <tr key={m.label} className="border-t border-line">
                <td className="py-2 font-medium text-ink">{m.label}</td>
                <td className="py-2 text-center">
                  {m.admin ? <Check size={14} className="mx-auto text-success" /> : <X size={14} className="mx-auto text-danger" />}
                </td>
                <td className="py-2 text-center">
                  {m.desk ? <Check size={14} className="mx-auto text-success" /> : <X size={14} className="mx-auto text-danger" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="rounded-3xl border border-line bg-card p-4 text-xs text-muted">
        Pickup zones and the WhatsApp confirmation are edited in Mon Agence. Staff accounts are edited in Staff. The signed-in role above comes from the login.
      </p>

      {/* Danger zone */}
      <div className="rounded-3xl border border-red-200 bg-red-50 p-4">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-danger">
          <AlertTriangle size={14} /> Zone dangereuse
        </p>
        {!confirmReset ? (
          <button
            onClick={() => setConfirmReset(true)}
            className="mt-2 flex items-center gap-2 rounded-2xl border border-red-300 bg-card px-4 py-2 text-xs font-bold text-danger"
          >
            <RotateCcw size={14} /> Clear local session cache
          </button>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <p className="text-xs text-red-700">This clears leftover browser keys and reloads. It does not reset the database.</p>
            <button
              onClick={() => {
                onResetDemo();
                setConfirmReset(false);
              }}
              className="rounded-2xl bg-ink px-4 py-2 text-xs font-bold text-shell"
            >
              Confirmer
            </button>
            <button
              onClick={() => setConfirmReset(false)}
              className="rounded-2xl border border-red-300 bg-card px-4 py-2 text-xs font-bold text-danger"
            >
              Annuler
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
